/**
 * <aya-beauty-bank-ledger> — Beauty Bank ledger 01.
 *
 * The section server-renders the ledger for its default month count; this
 * element only re-renders the last three deposit rows and the total when the
 * months slider moves (progressive enhancement, AGENTS.md §5). Labels come from
 * the section's settings via data-* attributes, "[n]" standing for the number.
 *
 * Unlike the reference page's inline script, every lookup is scoped to this
 * element, so two copies of the section (or the reference's separate desktop and
 * mobile copies) can't collide on a shared id.
 */
class AyaBeautyBankLedger extends HTMLElement {
  connectedCallback() {
    this.slider = this.querySelector('input[type="range"]');
    this.rows = this.querySelector('[data-role="rows"]');
    this.total = this.querySelector('[data-role="total"]');
    if (!this.slider || !this.rows || !this.total) return;

    this.onInput = () => this.render(Number(this.slider.value));
    this.slider.addEventListener('input', this.onInput);
  }

  disconnectedCallback() {
    this.slider?.removeEventListener('input', this.onInput);
  }

  render(months) {
    const deposit = Number(this.dataset.deposit) || 0;
    const currency = this.dataset.currency || '';
    const rowLabel = this.dataset.rowLabel || '';
    const totalLabel = months === 1 ? this.dataset.totalLabelSingular : this.dataset.totalLabel;
    const format = (value) => currency + value.toLocaleString('en-US');

    const rows = [];
    for (let month = Math.max(1, months - 2); month <= months; month += 1) {
      rows.push(this.row(rowLabel.replace('[n]', month), `+ ${format(deposit)}`));
    }
    this.rows.replaceChildren(...rows);

    const [label, amount] = this.total.children;
    label.textContent = (totalLabel || '').replace('[n]', months);
    amount.textContent = format(deposit * months);

    const unit = months === 1 ? this.slider.dataset.unitSingular : this.slider.dataset.unitPlural;
    this.slider.setAttribute('aria-valuetext', `${months} ${unit || ''}`.trim());
  }

  row(labelText, amountText) {
    const row = document.createElement('div');
    row.className = 'aya-beauty-bank-ledger__row';
    const label = document.createElement('span');
    label.textContent = labelText;
    const amount = document.createElement('span');
    amount.className = 'aya-beauty-bank-ledger__amount';
    amount.textContent = amountText;
    row.append(label, amount);
    return row;
  }
}

if (!customElements.get('aya-beauty-bank-ledger')) {
  customElements.define('aya-beauty-bank-ledger', AyaBeautyBankLedger);
}
